import { useState } from 'react';
import { formatServerDateShort } from '@/lib/dates';
import { useAdminInvites, useRevokeAdminInvite, useViewOnlyGate } from './hooks';
import { Button } from '@/components/ui/button';

/**
 * PendingInvitesList — admin Users-page card listing founder invites that
 * haven't been used yet (no signup, no company), with a per-row Revoke
 * (two-step confirm). Lets the admin clean up typo'd / abandoned invites;
 * signed-up or onboarded accounts aren't shown here (those go through the
 * tenant teardown). Mirrors the CompaniesTable confirm pattern.
 */
export function PendingInvitesList() {
  const invites = useAdminInvites();
  const revoke = useRevokeAdminInvite();
  const { gate } = useViewOnlyGate();
  const [confirmId, setConfirmId] = useState<number | null>(null);

  return (
    <div className="bg-white border border-line rounded-xl shadow-card p-6 sm:p-7">
      <span className="text-navy text-[13px] font-semibold uppercase tracking-[0.08em]">
        Pending invites
      </span>

      <div className="mt-4">
        {invites.isLoading && <p className="text-muted text-[13px] m-0">Loading…</p>}
        {invites.isError && (
          <p className="text-destructive text-[13px] m-0">Couldn&rsquo;t load invites.</p>
        )}
        {invites.isSuccess && invites.data.length === 0 && (
          <p className="text-muted text-[13px] italic m-0">No pending invites.</p>
        )}

        {invites.isSuccess && invites.data.length > 0 && (
          <ul className="divide-y divide-line m-0 p-0 list-none">
            {invites.data.map((inv) => {
              const confirming = confirmId === inv.id;
              return (
                <li key={inv.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="text-[13.5px] text-ink font-medium m-0 break-all">{inv.email}</p>
                    <p className="text-[12px] text-muted m-0">
                      {inv.ai_training_track === 'training' ? 'AI training' : 'Non-AI'}
                      {inv.expires_at ? ` · expires ${formatServerDateShort(inv.expires_at)}` : ''}
                    </p>
                  </div>
                  {confirming ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[12px] text-muted">Revoke?</span>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={revoke.isPending}
                        onClick={() =>
                          revoke.mutate(inv.id, { onSettled: () => setConfirmId(null) })
                        }
                      >
                        {revoke.isPending ? 'Revoking…' : 'Confirm'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      {...gate()}
                      onClick={() => setConfirmId(inv.id)}
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
